import React, { useEffect, useState } from 'react';
import { Text, TextVariant } from '@naf/text';
import { Button } from '@naf-simba/elements-buttons';
import { Loader } from '@naf-simba/building-blocks-loaders';
import { Input } from '@naf/input';
import { Link } from '@naf-simba/elements-links';
import { Tag } from '@naf-simba/elements-tag';
import { CheckCircle } from '@styled-icons/material/CheckCircle';
import { ProductType, useProductChangeCalculator } from '../../../../../../hooks/useProductChangeCalculator';
import { useSessionStorage } from '../../../../../../hooks/useSessionStorage';
import { WindshieldWiperChangeCalculationType } from '../../../../../../../../types/windshieldChangeCalculationTypes';
import { ArticleBlockContentGridCol } from '../ArticleBlockContentGridCol';
import useSelector from '../../../../../../redux/typedHooks';
import { BlockContentContext } from '../../../../BlockContent.utils';
import { normalizeLicensePlate } from '../../../../../../utils/normalizeLicensePlate';
import * as S from './WiperChangeCalculator.styles';

export const WindShieldWiperChangeCalculatorBlock = () => {
  const [centerId] = useSessionStorage('centerId', undefined);
  const [licensePlateNumber, setLicensePlateNumber] = useState<string>('');
  const [calculationResult, setCalculationResult] = useState<WindshieldWiperChangeCalculationType | undefined>();
  const { calculate, calculation, isLoading, error } = useProductChangeCalculator({
    productType: ProductType.WINDSHIELD,
    licensePlateNumber,
    centerId: centerId as string | undefined,
  });

  const isMember = useSelector((state) => state?.myMembership?.customerInformation?.data?.membership?.isValid ?? false);

  useEffect(() => {
    setCalculationResult(calculation as WindshieldWiperChangeCalculationType);
  }, [calculation]);

  const formatPrice = (price) => (Number.isInteger(price) ? price.toFixed(0) : price.toFixed(2));

  const getPriceComponents = () => {
    if (!calculationResult) return null;

    const isCampaign = calculationResult.memberPrice?.isCampaign || calculationResult.nonMemberPrice?.isCampaign;
    const memberPrice = calculationResult.memberPrice?.isCampaign
      ? (calculationResult.memberPrice?.campaignPrice ?? 0)
      : (calculationResult.memberPrice?.price ?? 0);
    const nonMemberPrice = calculationResult.nonMemberPrice?.isCampaign
      ? (calculationResult.nonMemberPrice?.campaignPrice ?? 0)
      : (calculationResult.nonMemberPrice?.price ?? 0);

    if (memberPrice === nonMemberPrice || memberPrice > nonMemberPrice) {
      return (
        <Text variant={TextVariant.Header3}>Pris for vindusviskerskift foran: kr {formatPrice(nonMemberPrice)},-</Text>
      );
    }

    return (
      <>
        <Text variant={TextVariant.Header3}>
          {isCampaign ? (
            <S.Row>
              {`${isMember ? 'Din pris:' : 'Medlemspris:'} kr ${formatPrice(memberPrice)},-`}
              <S.StruckText variant={TextVariant.Header3}>
                {formatPrice(calculationResult.memberPrice.price)},-
              </S.StruckText>
            </S.Row>
          ) : (
            `${isMember ? 'Din pris:' : 'Medlemspris:'} kr ${formatPrice(memberPrice)},-`
          )}
        </Text>
        <Text variant={TextVariant.Header3}>Pris for ikke-medlemmer: kr {formatPrice(nonMemberPrice)},-</Text>
      </>
    );
  };

  return (
    <S.WindshieldWiperChange>
      {(calculationResult?.memberPrice?.isCampaign || calculationResult?.nonMemberPrice?.isCampaign) && (
        <Tag color="secondary" shade="light" text="Kampanje" />
      )}
      <Text variant={TextVariant.Header2}>Se hvor mye vindusviskerskift på din bil koster</Text>
      <Text variant={TextVariant.ArticleTextLarge}>
        Legg inn registeringsnummer for å beregne hvor mye det vil koste å skifte vindusviskere for din bil.
      </Text>
      <S.Search>
        <Input
          placeholder="DN12345"
          id="registrationNumber"
          type="text"
          onChange={(number: string) => {
            const normalized = normalizeLicensePlate(number);
            setLicensePlateNumber(normalized);
          }}
          onKeyUp={(e: KeyboardEvent) => {
            if (e.key === 'Enter') calculate();
          }}
        />
        <Button type="button" color="secondary" onClick={() => calculate()}>
          Beregn pris
        </Button>
      </S.Search>
      {isLoading && (
        <S.SpinnerWrapper>
          <Loader />
        </S.SpinnerWrapper>
      )}
      {error && !isLoading && (
        <S.LookupResultError>
          <Text variant={TextVariant.Header3}>Vi klarte ikke finne en bil på dette registreringsnummeret.</Text>
          <Text>Ta kontakt med kundeservice for en prisforespørsel på vindusviskerskift for ditt kjøretøy.</Text>
          <Link to="/kundeservice" text="Kontakt kundeservice" />
        </S.LookupResultError>
      )}
      {!error && !isLoading && calculationResult && (
        <S.LookupResultSuccess>
          <S.VehicleText>
            <CheckCircle size={22} />
            {calculationResult.vehicle && (
              <S.SuccessText variant={TextVariant.BodyText}>
                {calculationResult.vehicle?.manufactureName} {calculationResult.vehicle?.modelName}{' '}
                {calculationResult.vehicle?.year}
              </S.SuccessText>
            )}
          </S.VehicleText>
          {calculationResult.memberPrice?.price && calculationResult.nonMemberPrice?.price && (
            <S.Summary>{getPriceComponents()}</S.Summary>
          )}
          {(!calculationResult.memberPrice?.price || !calculationResult.nonMemberPrice?.price) && (
            <>
              <Text variant={TextVariant.Header3}>Vi klarer ikke å kalkulere riktig pris for din bil</Text>
              <Text>Ta kontakt med kundeservice for en prisforespørsel på vindusviskerskift for ditt kjøretøy</Text>
              <Link to="/kundeservice" text="Kontakt kundeservice" />
            </>
          )}
          {calculationResult.link && calculationResult.link.data && (
            <Button
              href={calculationResult.link.data.href}
              target={calculationResult.link.data.blank ? '_blank' : '_self'}
            >
              Finn ledig tid
            </Button>
          )}
        </S.LookupResultSuccess>
      )}
    </S.WindshieldWiperChange>
  );
};

export const WindShieldWiperChangeCalculatorSerializer = (
  context: BlockContentContext = BlockContentContext.Stripped,
) => {
  if (context === BlockContentContext.Article) {
    return () => (
      <ArticleBlockContentGridCol>
        <WindShieldWiperChangeCalculatorBlock />
      </ArticleBlockContentGridCol>
    );
  }
  return () => <WindShieldWiperChangeCalculatorBlock />;
};
